import { isClerkAPIResponseError } from '@clerk/nextjs/errors'
import { useTranslation } from 'next-i18next'

/**
 * Maps Clerk error codes to translation keys
 * Error codes are grouped by category to reduce translation file size
 * Based on: https://clerk.com/docs/guides/development/errors/frontend-api
 */
const clerkErrorCodeMap: Record<string, string> = {
  // Authentication errors
  invalid_authentication: 'errors.clerk.invalidCredentials',
  invalid_authorization: 'errors.clerk.unauthorized',
  session_exists: 'errors.clerk.sessionExists',
  session_not_found: 'errors.clerk.sessionNotFound',
  signed_out: 'errors.clerk.signedOut',
  invalid_authorization_header_format: 'errors.clerk.invalidFormat',
  invalid_csrf_token: 'errors.clerk.invalidFormat',
  invalid_handshake: 'errors.clerk.invalidFormat',
  invalid_host: 'errors.clerk.invalidFormat',
  invalid_origin_header: 'errors.clerk.invalidFormat',
  invalid_request_for_environment: 'errors.clerk.invalidFormat',
  invalid_user_settings: 'errors.clerk.invalidValue',
  missing_request_headers_for_non_standard_browsers: 'errors.clerk.invalidFormat',
  missing_request_headers_for_standard_browsers: 'errors.clerk.invalidFormat',
  multiple_authorization_header_values: 'errors.clerk.invalidFormat',
  multiple_origin_header_values: 'errors.clerk.invalidFormat',
  native_api_disabled: 'errors.clerk.featureDisabled',
  origin_and_authorization_mutually_exclusive: 'errors.clerk.invalidFormat',
  url_based_session_syncing_disabled: 'errors.clerk.featureDisabled',
  identification_exists: 'errors.clerk.identifierExists',
  identifier_contains_subaddresses: 'errors.clerk.invalidEmail',
  identifier_not_allowed_access: 'errors.clerk.unauthorized',
  subaddress_restriction_email_already_used: 'errors.clerk.identifierExists',

  // Form validation errors - grouped by type
  form_invalid_email_address: 'errors.clerk.invalidEmail',
  form_invalid_email_addresses: 'errors.clerk.invalidEmail',
  form_invalid_parameter_format: 'errors.clerk.invalidFormat',
  form_invalid_parameter_value: 'errors.clerk.invalidValue',
  form_param_format_invalid: 'errors.clerk.invalidFormat',
  form_disabled_parameter_value: 'errors.clerk.invalidValue',
  form_duplicate_parameter: 'errors.clerk.invalidValue',
  form_identifier_not_found: 'errors.clerk.invalidCredentials',
  form_identifier_exists: 'errors.clerk.identifierExists',
  form_incorrect_code: 'errors.clerk.verificationFailed',
  form_incorrect_signature: 'errors.clerk.verificationFailed',
  form_invalid_encoding_parameter_value: 'errors.clerk.invalidValue',
  form_invalid_parameter_value_with_allowed: 'errors.clerk.invalidValue',
  form_invalid_type_parameter: 'errors.clerk.invalidFormat',
  form_invalid_username_character: 'errors.clerk.invalidFormat',
  form_invalid_username_length: 'errors.clerk.invalidValue',
  form_invalid_username_needs_non_number_char_code: 'errors.clerk.invalidFormat',
  form_invalid_web3_wallet_address: 'errors.clerk.invalidValue',
  form_legal_not_accepted: 'errors.clerk.invalidValue',
  form_maximum_parameters_exceeded: 'errors.clerk.maxLengthExceeded',
  form_meta: 'errors.clerk.invalidValue',
  form_missing: 'errors.clerk.missingField',
  form_password_does_not_match: 'errors.clerk.passwordNoMatch',
  form_password_size_in_bytes_exceeded: 'errors.clerk.passwordTooLong',
  form_param_none_not_supported: 'errors.clerk.invalidValue',
  form_param_size_in_bytes_too_large: 'errors.clerk.maxLengthExceeded',

  // Password errors
  form_password_length_too_short: 'errors.clerk.passwordTooShort',
  form_password_length_too_long: 'errors.clerk.passwordTooLong',
  form_password_pwned: 'errors.clerk.passwordCompromised',
  form_password_incorrect: 'errors.clerk.invalidCredentials',
  form_password_not_strong_enough: 'errors.clerk.passwordNotStrong',
  form_password_no_uppercase: 'errors.clerk.passwordNoUppercase',
  form_password_no_lowercase: 'errors.clerk.passwordNoLowercase',
  form_password_no_number: 'errors.clerk.passwordNoNumber',
  form_password_no_special_char: 'errors.clerk.passwordNoSpecialChar',
  no_password_set: 'errors.clerk.noPasswordSet',
  form_invalid_password_length_too_short: 'errors.clerk.passwordTooShort',
  form_invalid_password_length_too_long: 'errors.clerk.passwordTooLong',
  form_invalid_password_not_strong_enough: 'errors.clerk.passwordNotStrong',
  form_invalid_password_no_uppercase: 'errors.clerk.passwordNoUppercase',
  form_invalid_password_no_lowercase: 'errors.clerk.passwordNoLowercase',
  form_invalid_password_no_number: 'errors.clerk.passwordNoNumber',
  form_invalid_password_no_special_char: 'errors.clerk.passwordNoSpecialChar',
  password_required: 'errors.clerk.missingField',

  // Verification errors
  verification_failed: 'errors.clerk.verificationFailed',
  verification_expired: 'errors.clerk.verificationExpired',
  verification_not_sent: 'errors.clerk.verificationNotSent',
  verification_already_verified: 'errors.clerk.verificationFailed',
  verification_code_too_many_requests: 'errors.clerk.tooManyAttempts',
  verification_invalid_link_token: 'errors.clerk.verificationFailed',
  verification_invalid_link_token_source: 'errors.clerk.verificationFailed',
  verification_invalid_strategy: 'errors.clerk.verificationFailed',
  verification_link_token_expired: 'errors.clerk.verificationExpired',
  verification_missing: 'errors.clerk.verificationNotSent',
  verification_unknown_status: 'errors.clerk.verificationFailed',

  // Rate limiting / throttling
  rate_limit_exceeded: 'errors.clerk.rateLimited',

  // Account status errors
  user_not_found: 'errors.clerk.userNotFound',
  user_locked: 'errors.clerk.accountLocked',
  user_banned: 'errors.clerk.accountBanned',
  user_locked_indefinitely: 'errors.clerk.accountLocked',
  user_create_organization_not_enabled: 'errors.clerk.featureDisabled',
  user_delete_self_not_enabled: 'errors.clerk.featureDisabled',
  user_quota_exceeded: 'errors.clerk.accountLocked',

  // Session errors
  session_token_expired: 'errors.clerk.sessionExpired',
  session_token_invalid: 'errors.clerk.sessionInvalid',
  session_reverification_required: 'errors.clerk.sessionReverificationRequired',
  session_invalid: 'errors.clerk.sessionInvalid',
  session_idle_timeout: 'errors.clerk.sessionExpired',
  session_active_count_limit_reached: 'errors.clerk.sessionLimitReached',
  session_already_exists: 'errors.clerk.sessionExists',

  // Client/Resource errors
  client_not_found: 'errors.clerk.resourceNotFound',
  client_not_found_in_request: 'errors.clerk.resourceNotFound',
  resource_not_found: 'errors.clerk.resourceNotFound',
  resource_forbidden: 'errors.clerk.unauthorized',

  // Feature errors
  feature_not_enabled: 'errors.clerk.featureDisabled',
  feature_requires_dynamic_oauth_client_registration: 'errors.clerk.featureDisabled',
  feature_requires_oauth2_consent_screen_enabled: 'errors.clerk.featureDisabled',
  feature_requires_oidc_provider: 'errors.clerk.featureDisabled',

  // URL errors
  invalid_url_scheme: 'errors.clerk.invalidUrl',

  // Sign In errors
  strategy_for_user_invalid: 'errors.clerk.verificationFailed',
  account_transfer_invalid: 'errors.clerk.invalidValue',
  identifier_already_signed_in: 'errors.clerk.sessionExists',
  identification_claimed: 'errors.clerk.identifierExists',
  client_state_invalid: 'errors.clerk.invalidValue',
  no_second_factors: 'errors.clerk.verificationFailed',
  sign_in_email_link_not_same_client: 'errors.clerk.verificationFailed',
  sign_in_identification_or_user_deleted: 'errors.clerk.resourceNotFound',
  sign_in_no_identification_for_user: 'errors.clerk.resourceNotFound',
  mutation_on_older_sign_in_not_allowed: 'errors.clerk.unauthorized',

  // Sign Up errors
  captcha_invalid: 'errors.clerk.verificationFailed',
  captcha_missing_token: 'errors.clerk.verificationFailed',
  captcha_not_enabled: 'errors.clerk.featureDisabled',
  sign_up_email_link_not_same_client: 'errors.clerk.verificationFailed',
  sign_up_forbidden_access: 'errors.clerk.unauthorized',
  sign_up_mode_restricted: 'errors.clerk.featureDisabled',
  sign_up_restricted_waitlist: 'errors.clerk.featureDisabled',

  // Actor Tokens
  actor_token_already_used_code: 'errors.clerk.verificationExpired',
  actor_token_not_in_sign_in_code: 'errors.clerk.verificationFailed',
  actor_token_cannot_be_used_code: 'errors.clerk.verificationExpired',
  actor_token_revoked_code: 'errors.clerk.verificationExpired',
  actor_token_subject_not_found: 'errors.clerk.resourceNotFound',

  // Sign In Tokens
  sign_in_token_already_used_code: 'errors.clerk.verificationExpired',
  sign_in_token_not_in_sign_in_code: 'errors.clerk.verificationFailed',
  sign_in_token_cannot_be_used_code: 'errors.clerk.verificationExpired',
  sign_in_token_revoked_code: 'errors.clerk.verificationExpired',

  // Session errors (additional)
  session_creation_not_allowed: 'errors.clerk.sessionExists',
  deprovisioned: 'errors.clerk.accountLocked',
  invalid_action_for_session: 'errors.clerk.verificationFailed',
  invalid_action_for_session_reverification: 'errors.clerk.verificationFailed',
  session_reverification_missing: 'errors.clerk.verificationNotSent',
  action_for_session_not_authorized: 'errors.clerk.unauthorized',

  // Form errors (additional)
  form_param_missing: 'errors.clerk.missingField',
  form_resource_not_found: 'errors.clerk.resourceNotFound',
  form_param_nil: 'errors.clerk.missingField',
  form_param_unknown: 'errors.clerk.invalidValue',
  form_param_duplicate: 'errors.clerk.invalidValue',
  form_param_array_size_exceeded: 'errors.clerk.maxLengthExceeded',
  form_param_max_length_exceeded: 'errors.clerk.maxLengthExceeded',
  form_param_exceeds_allowed_size: 'errors.clerk.maxLengthExceeded',
  form_conditional_param_disallowed: 'errors.clerk.invalidValue',
  form_password_validation_failed: 'errors.clerk.invalidCredentials',
  form_metadata_invalid_type: 'errors.clerk.invalidValue',
  form_param_type_invalid: 'errors.clerk.invalidFormat',
  form_validation_failed: 'errors.clerk.invalidValue',

  // Verification errors (additional)
  verification_status_unknown: 'errors.clerk.verificationFailed',

  // OAuth errors
  external_account_email_address_verification_required: 'errors.clerk.verificationNotSent',
  external_account_missing_refresh_token: 'errors.clerk.verificationFailed',
  external_account_not_found: 'errors.clerk.resourceNotFound',
  oauth_callback_invalid: 'errors.clerk.verificationFailed',
  misconfigured_oauth_provider: 'errors.clerk.featureDisabled',
  oauth_non_authenticatable_provider: 'errors.clerk.featureDisabled',
  oauth_access_denied: 'errors.clerk.unauthorized',
  oauth_account_already_connected: 'errors.clerk.identifierExists',
  oauth_config_missing: 'errors.clerk.featureDisabled',
  oauth_fetch_user_error: 'errors.clerk.verificationFailed',
  oauth_identification_claimed: 'errors.clerk.identifierExists',
  redirect_uri_mismatch: 'errors.clerk.invalidUrl',
  oauth_missing_access_token: 'errors.clerk.verificationFailed',
  oauth_missing_refresh_token: 'errors.clerk.verificationFailed',
  oauth_provider_not_enabled: 'errors.clerk.featureDisabled',
  oauth_shared_credentials_not_supported: 'errors.clerk.featureDisabled',
  oauth_token_exchange_error: 'errors.clerk.verificationFailed',
  oauth_unsupported_provider: 'errors.clerk.featureDisabled',

  // Invitations
  invitation_account_exists: 'errors.clerk.identifierExists',
  invitation_already_accepted: 'errors.clerk.verificationFailed',
  invitation_account_not_exists: 'errors.clerk.resourceNotFound',
  revoked_invitation: 'errors.clerk.verificationExpired',

  // Fraud/Device Attestation
  requires_captcha: 'errors.clerk.verificationFailed',
  action_blocked: 'errors.clerk.accountLocked',
  signup_rate_limit_exceeded: 'errors.clerk.rateLimited',

  // Identifications
  primary_identification_not_found: 'errors.clerk.resourceNotFound',
  too_many_unverified_identifications: 'errors.clerk.rateLimited',

  // Redirect URLs
  invalid_redirect_url: 'errors.clerk.invalidUrl',
  redirect_url_domain_mismatch: 'errors.clerk.invalidUrl',
  resource_missmatch: 'errors.clerk.invalidUrl',

  // Requests
  invalid_query_parameter_value: 'errors.clerk.invalidValue',
  request_body_invalid: 'errors.clerk.invalidFormat',
  malformed_request_parameters: 'errors.clerk.invalidFormat',
  missing_query_parameter: 'errors.clerk.missingField',
  origin_missing: 'errors.clerk.missingField',
  proxy_request_invalid_secret_key: 'errors.clerk.unauthorized',
  proxy_request_missing_secret_key: 'errors.clerk.missingField',
  unsupported_content_type: 'errors.clerk.invalidFormat',

  // Maintenance
  maintenance_mode: 'errors.clerk.featureDisabled',

  // Waitlist
  waitlist_not_accepting_entries: 'errors.clerk.featureDisabled',

  // Users (additional)
  updating_user_password_deprecated: 'errors.clerk.featureDisabled',

  // Generic fallbacks
  forbidden: 'errors.clerk.unauthorized',
  identifier_not_found: 'errors.clerk.identifierNotFound',
  identifier_exists: 'errors.clerk.identifierExists',
  bad_request: 'errors.clerk.invalidFormat',
  internal_clerk_error: 'errors.clerk.verificationFailed',
}

/**
 * Hook to get a localized error message from a Clerk error
 * Returns null if the error is not a Clerk error
 *
 * @returns A function that takes an error and returns a localized message or null
 */
export function useClerkErrorMessage() {
  const { t } = useTranslation('forms')

  return (error: unknown): string | null => {
    if (!isClerkAPIResponseError(error)) {
      if (error instanceof Error) {
        return error.message
      }
      return null
    }

    const firstError = error.errors?.[0]
    if (!firstError) {
      return null
    }

    console.error(`[Clerk error] ${firstError.code}`, firstError)

    const errorCode = firstError.code
    if (!errorCode) {
      return null
    }

    // Try to get a translated message for this error code
    const translationKey = clerkErrorCodeMap[errorCode]
    if (translationKey) {
      const translatedMessage = t(translationKey, { defaultValue: '' })
      if (translatedMessage) {
        if (firstError.meta && 'paramName' in firstError.meta) {
          const fieldName = firstError.meta.paramName as string

          // Map common field names to specific error messages
          const fieldErrorMap: Record<string, string> = {
            password: 'errors.clerk.passwordInvalid',
            email: 'errors.clerk.emailInvalid',
            identifier: 'errors.clerk.identifierInvalid',
          }

          const fieldErrorKey = fieldErrorMap[fieldName]
          if (fieldErrorKey) {
            const fieldMessage = t(fieldErrorKey, { defaultValue: '' })
            if (fieldMessage) {
              return fieldMessage
            }
          }
        }

        return translatedMessage
      }
    }

    // Fallback to the longMessage if translation exists but is empty
    return firstError.longMessage || firstError.message || null
  }
}
